import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { OverlayView, Polygon, Polyline } from '@react-google-maps/api';
import { useShouldHighlight } from '@hooks/map-hooks';
import { mapConfig } from '@constants/component-configs';
import {
  getMapBoundariesVisible,
  getMapLabelsVisible
} from '@selectors/groups-selector';
import { getHoverGroupId, getTrayGroupIds } from '@selectors/map-selector';
import { generatePath, generatePolygonPaths } from '@utils/map-utils';
import './group-figure.scss';

// Distinct color palette for groups to improve accessibility.
const GROUP_COLORS = [
  '#FF0000', '#0066CC', '#008844', '#CC6600', '#9933CC',
  '#CC0066', '#006666', '#884400', '#3366FF', '#669900',
  '#FF6633', '#3399CC', '#339966', '#996600', '#6633CC',
  '#CC3399', '#009999', '#AA5500', '#0044AA', '#88AA00'
];

const getGroupColor = (groupId) => GROUP_COLORS[(groupId || 0) % GROUP_COLORS.length];

const buildIcons = (color) => [{
  icon: {
    path: 'M 0,-2 0,2',
    strokeOpacity: 1,
    strokeColor: color,
    scale: 2
  },
  offset: '0',
  repeat: '20px'
}];

const buildHighlightIcons = (color) => [{
  icon: {
    path: 'M 0,-1 0,1',
    strokeOpacity: 1,
    strokeColor: color,
    scale: 4
  },
  offset: '0',
  repeat: '20px'
}];

const buildGroupOptions = (color) => ({
  fillColor: color, strokeColor: color, strokeOpacity: 0.0, icons: buildIcons(color), fillOpacity: 0.1
});

const buildGroupHighlightOptions = (color) => ({
  ...buildGroupOptions(color), icons: buildHighlightIcons(color), fillOpacity: 0.3, zIndex: 200
});

const MapGroup = ({ autoArea, center, id, name }) => {
  const boundariesVisible = useSelector(getMapBoundariesVisible);
  const labelsVisible = useSelector(getMapLabelsVisible);

  // Use the custom hook for highlight logic
  const { shouldHighlight } = useShouldHighlight(id, getTrayGroupIds, getHoverGroupId);

  const { shape } = autoArea;

  const groupColor = useMemo(() => getGroupColor(id), [id]);

  const options = useMemo(
    () => ({ ...mapConfig.defaultStyle, ...(shouldHighlight ? buildGroupHighlightOptions(groupColor) : buildGroupOptions(groupColor)) }),
    [shouldHighlight, groupColor]
  );

  const { rings, polygons } = useMemo(() => {
    if (shape.type === 'Polygon') {
      return {
        rings: shape.coordinates.map(generatePath),
        polygons: [
          <Polygon key="polygon-0" paths={generatePolygonPaths(shape.coordinates)} options={options} />
        ]
      };
    } else if (shape.type === 'MultiPolygon') {
      // For MultiPolygon, collect all rings from all polygons and create separate polygons
      return {
        rings: shape.coordinates.flatMap(polygonCoords =>
          polygonCoords.map(ringCoords => generatePath(ringCoords))
        ),
        polygons: shape.coordinates.map((polygonCoords, index) => (
          <Polygon
            key={`polygon-${index}`}
            paths={generatePolygonPaths(polygonCoords)}
            options={options}
          />
        ))
      };
    }
    return { rings: [], polygons: [] };
  }, [shape, options]);

  const labelClass = `group-label ${shouldHighlight ? 'active' : ''}`;

  return (
    <div>
      {boundariesVisible && (
        <div>
          <div>
            {rings.map((path, index) => <Polyline key={`polyline-${index}`} path={path} options={options} />)}
          </div>
          {polygons}
        </div>
      )}
      {labelsVisible && (
        <OverlayView
          mapPaneName={OverlayView.FLOAT_PANE}
          position={{ lng: center[0], lat: center[1] }}
          zIndex={200}
        >
          <div styleName={labelClass}>
            {name}
          </div>
        </OverlayView>
      )}
    </div>
  );
};

MapGroup.propTypes = {
  autoArea: PropTypes.shape({
    shape: PropTypes.shape({
      coordinates: PropTypes.array,
      type: PropTypes.string
    })
  }),
  center: PropTypes.array,
  id: PropTypes.number,
  name: PropTypes.string
};

export default memo(MapGroup);
